import { Link, useSearchParams } from "react-router-dom";
import useSWR from "swr";
import { toast } from "sonner";
import { Layers, Shuffle, Heart, Printer, X, Send, Package } from "lucide-react";
import { useState, useRef } from "react";
import { QuoteDialog } from "@/components/QuoteDialog";
import { uploadSnapshot } from "@/components/kitchen3d/snapshot";
import { fetcher, asList, imgUrl } from "@/lib/api";
import { useShortlist } from "@/context/ShortlistContext";
import { useSavedPairs, pairKey } from "@/lib/savedPairs";
import { getFinishes, finishBg } from "@/components/FinishDots";
import { PairPreview } from "@/components/PairPreview";
import { PairPicker } from "@/components/PairPicker";
import { MaskTitle } from "@/components/MaskTitle";
import { Eyebrow } from "@/components/Reveal";

const Chosen = ({ label, item }) => (
  <div className="flex items-center gap-4 min-w-0" data-testid={`pair-chosen-${label.toLowerCase()}`}>
    <div className="w-16 h-16 shrink-0 border border-[#D4AF37]/25 bg-[#0D0D11]" style={item ? { backgroundImage: `url(${imgUrl(item.images?.[0])})`, backgroundSize: "cover" } : {}} />
    <div className="min-w-0">
      <p className="font-mono-j text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">{label}</p>
      {item ? (
        <Link to={`/item/${item.id}`} className="block font-display text-2xl text-[#F5F3EF] truncate hover:text-[#E5C158] transition-colors">{item.name}</Link>
      ) : <p className="font-display italic text-2xl text-[#A09E97]">Not chosen</p>}
      {item && <p className="text-xs text-[#A09E97] truncate">{[item.supplier, item.code].filter(Boolean).join(" · ")}</p>}
    </div>
  </div>
);

export default function PairingPage() {
  const [params, setParams] = useSearchParams();
  const { addMany } = useShortlist();
  const { data: bData } = useSWR("/items?category=board-products", fetcher);
  const { data: sData } = useSWR("/items?category=stone-options", fetcher);
  const { data: hData } = useSWR("/items?category=hardware", fetcher);
  const { pairs, save, remove, setSnapshot } = useSavedPairs();
  const [quote, setQuote] = useState(false);
  const [samples, setSamples] = useState(false);
  const captureRef = useRef(null);
  const boards = asList(bData);
  const stones = asList(sData);
  const handles = asList(hData);
  const board = boards.find((b) => b.id === params.get("board"));
  const stone = stones.find((s) => s.id === params.get("stone"));
  const handle = handles.find((h) => h.id === params.get("handle"));
  const finishes = getFinishes(handle);
  const finish = finishes.includes(params.get("finish")) ? params.get("finish") : finishes[0] || "";
  const all = [...boards, ...stones, ...handles];
  const byId = (id) => all.find((i) => i.id === id);
  const pick = (k, item) => {
    const p = new URLSearchParams(params);
    p.set(k, item.id);
    if (k === "handle") p.delete("finish");
    setParams(p, { replace: true });
  };
  const setFinish = (f) => {
    const p = new URLSearchParams(params);
    p.set("finish", f);
    setParams(p, { replace: true });
  };
  const savePair = async () => {
    const p = { board: board.id, stone: stone.id, handle: handle?.id || "", finish: handle ? finish : "" };
    save(p);
    toast.success("Pairing saved");
    if (captureRef.current && !pairs.find((x) => pairKey(x) === pairKey(p))?.snapshot) {
      try {
        setSnapshot(pairKey(p), await uploadSnapshot(captureRef.current(), { board, stone, handle, finish }));
      } catch {
        toast.message("Saved without a kitchen photo", { description: "The 3D view couldn't be captured this time." });
      }
    }
  };
  const load = (p) => setParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v)), { replace: true });
  const surprise = () => {
    const r = (a) => a[Math.floor(Math.random() * a.length)];
    if (boards.length && stones.length) setParams({ board: r(boards).id, stone: r(stones).id }, { replace: true });
  };
  const add = () => {
    addMany([board, stone, handle].filter(Boolean).map((i) => i.id));
    toast.success(handle ? "Board, stone and handle added to your selection" : "Board and stone added to your selection");
  };

  return (
    <main className="pt-28 mx-auto max-w-[1440px] px-5 sm:px-10 min-h-screen">
      <Eyebrow>Pairing studio</Eyebrow>
      <MaskTitle text="Board meets stone" className="mt-4 font-display font-light text-5xl sm:text-7xl text-[#F5F3EF]" />
      <p className="mt-4 max-w-lg text-[#A09E97]">Choose a cabinet board and a benchtop stone to see them together on a kitchen run, then save the pair to your selection.</p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 min-w-0">
          <PairPreview board={board} stone={stone} handle={handle} finish={finish} captureRef={captureRef} />
          <div className="mt-4 grid grid-cols-2 gap-2 h-28 sm:h-40">
            {[board, stone].map((i, n) => (
              <div key={n} className="border border-[#D4AF37]/20 bg-[#13141C]" style={i ? { backgroundImage: `url(${imgUrl(i.images?.[0])})`, backgroundSize: "cover", backgroundPosition: "center" } : {}} />
            ))}
          </div>
        </div>
        <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start border border-[#D4AF37]/25 bg-[#13141C] p-6 space-y-6">
          <Chosen label="Board" item={board} />
          <Chosen label="Stone" item={stone} />
          <Chosen label="Handle" item={handle} />
          {finishes.length > 0 && (
            <div className="flex flex-wrap gap-2 -mt-2" data-testid="pair-finish-options">
              {finishes.map((f) => (
                <button key={f} onClick={() => setFinish(f)} title={f} data-testid={`pair-finish-${f.toLowerCase().replace(/\W+/g, "-")}`}
                  className={`inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 text-xs border transition-colors ${finish === f ? "border-[#D4AF37] text-[#F5F3EF]" : "border-[#D4AF37]/20 text-[#A09E97] hover:text-[#F5F3EF]"}`}>
                  <span className="w-5 h-5 rounded-full ring-1 ring-white/20" style={{ background: finishBg(f) }} />{f}
                </button>
              ))}
            </div>
          )}
          <button onClick={savePair} disabled={!board || !stone} data-testid="pair-save-btn"
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 disabled:opacity-50 transition-colors">
            <Heart className="w-4 h-4" /> Save this pairing
          </button>
          <button onClick={() => setSamples(true)} disabled={!board || !stone} data-testid="pair-samples-btn"
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 disabled:opacity-50 transition-colors">
            <Package className="w-4 h-4" /> Request samples of {handle ? "all three" : "both"}
          </button>
          <button onClick={add} disabled={!board || !stone} data-testid="pair-add-btn"
            className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] text-sm font-medium text-[#0D0D11] hover:bg-[#E5C158] disabled:opacity-50 transition-colors">
            <Layers className="w-4 h-4" /> Add {handle ? "all three" : "both"} to selection
          </button>
          <button onClick={surprise} data-testid="pair-surprise-btn"
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-full border border-[#D4AF37]/40 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors">
            <Shuffle className="w-4 h-4" /> Surprise me
          </button>
        </aside>
      </div>

      {pairs.length > 0 && (
        <section className="mt-12 border border-[#D4AF37]/25 bg-[#13141C] p-5 sm:p-8" data-testid="saved-pairs">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
            <h2 className="font-display text-3xl text-[#F5F3EF]">Saved pairings <span className="text-[#A09E97] text-xl">({pairs.length})</span></h2>
            <div className="flex flex-wrap gap-3">
              <button onClick={() => setQuote(true)} data-testid="saved-pairs-quote"
                className="h-11 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 text-sm font-medium text-[#0D0D11] hover:bg-[#E5C158] transition-colors"><Send className="w-4 h-4" /> Request a quote for these pairings</button>
              <Link to="/pairing/print" data-testid="saved-pairs-print"
                className="h-11 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 px-6 text-sm text-[#E5C158] hover:bg-[#D4AF37]/10 transition-colors"><Printer className="w-4 h-4" /> Print A4 sheet</Link>
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-2" data-lenis-prevent>
            {pairs.map((p, n) => (
              <div key={pairKey(p)} className="relative shrink-0" data-testid={`saved-pair-${n}`}>
                <button onClick={() => load(p)} className="flex border border-[#D4AF37]/25 hover:border-[#D4AF37] transition-colors" aria-label="Load pairing">
                  {p.snapshot ? (
                    <img src={imgUrl(p.snapshot)} alt="" className="h-16 sm:h-20 w-auto block" data-testid={`saved-pair-snapshot-${n}`} />
                  ) : [p.board, p.stone, p.handle].filter(Boolean).map((id) => (
                    <span key={id} className="w-16 h-16 sm:w-20 sm:h-20 bg-[#0D0D11]" style={byId(id) ? { backgroundImage: `url(${imgUrl(byId(id).images?.[0])})`, backgroundSize: "cover", backgroundPosition: "center" } : {}} />
                  ))}
                </button>
                <button onClick={() => remove(pairKey(p))} aria-label="Remove pairing" data-testid={`saved-pair-remove-${n}`}
                  className="absolute -top-2 -right-2 w-6 h-6 grid place-items-center rounded-full bg-[#0D0D11] border border-[#D4AF37]/40 text-[#F5F3EF] hover:text-red-400"><X className="w-3 h-3" /></button>
                <p className="mt-1 text-[11px] text-[#A09E97] max-w-[15rem] truncate">{byId(p.board)?.name} + {byId(p.stone)?.name}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="mt-12 space-y-10">
        <PairPicker id="board" label="Boards" items={boards} value={board} onPick={(i) => pick("board", i)} />
        <PairPicker id="stone" label="Stones" items={stones} value={stone} onPick={(i) => pick("stone", i)} />
        <PairPicker id="handle" label="Handles" items={handles} value={handle} onPick={(i) => pick("handle", i)} />
      </div>
      <QuoteDialog open={quote} onClose={() => setQuote(false)} pairings={pairs}
        items={[...new Set(pairs.flatMap((p) => [p.board, p.stone, p.handle]).filter(Boolean))].map(byId).filter(Boolean)} />
      <QuoteDialog open={samples} onClose={() => setSamples(false)} mode="samples" items={[board, stone, handle].filter(Boolean)} />
    </main>
  );
}
